// 05a bis · Le wizard clavier du dépouillement — MODE ALTERNATIF.
//
// Porté depuis la V2 (journee/[date]/depouillement-v2). Il n'est ni promu ni
// supprimé : certaines personnes dépouillent au clavier deux fois plus vite
// qu'à la souris, leur retirer cet écran serait une régression ; en faire le
// mode par défaut en serait une autre pour tous les autres.
//
// C'est le SEUL écran de FlashCP où les raccourcis clavier sont actifs (§13).
// Quatre phases dans l'ordre : les caissiers au clavier, le scan TRP / CAP,
// les décisions de remise, la fin de session.
//
// Conventions V2 tenues à la lettre :
//   • isPadPayment est la source unique du « est-ce un PAD » — les PAD ne
//     passent JAMAIS par ici, ils ont leur étape 03 avec ses quatre issues ;
//   • un règlement porte 'valide' / 'non_conforme' dans rapport.verifications
//     ('confirmed' / 'rejected' sont les anciens jetons, encore lus).
//     set_payment_verification n'accepte que 'valide' | 'en_attente' |
//     'non_conforme' — et n'écrit JAMAIS null : le test `NOT IN` laisse passer
//     NULL (comparaison SQL à trois valeurs) et poserait un null JSON dans le
//     blob. Le wizard n'émet que 'valide' et 'non_conforme' ;
//   • 'en_attente' est le troisième jeton, posé par l'écran principal 05a. Le
//     wizard ne le pose pas mais le rencontre : comme en V2 il compte comme
//     « pas encore validé » — pastille grise, et « + + » le passe non conforme ;
//   • la décision de remise vit dans rapport.paymentMeta[id].addToRemise, où
//     l'ABSENCE de clé signifie « pas encore décidé » et se compte comme tel.
//
// Sémantique des écarts : tout ce qui est un écart passe par @/domain/ecart.
// Le vert ne dit qu'une chose — « il n'y a pas d'écart ». La couleur ne dépend
// jamais du signe : cet écran affichait un excédent de caisse en vert.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isPadPayment, isUnbadgedOperateur } from 'sotcp-core/csvParser'
import { useRun } from '@/data/RunProvider'
import * as api from '@/data/api'
import { useReglages } from '@/data/reglages'
import { fmtEur } from '@/lib/format'
import { couleurEcart, couleurEcartUnites, EPSILON, fmtEcart, motEcart } from '@/domain/ecart'
import { Card, Chantier, Chip, Kpis, Lamp, Modale, Notice, Vide } from '@/ui/primitives'
import type { Rapport } from '@/domain/types'

/** Le wizard vit dans la branche « dep » de l'étape 05 — c'est elle qu'il touche. */
const ETAPE = '05B'

/**
 * Une enveloppe de prélèvement vaut 500 € — c'est l'unité de `prelevCount`,
 * que le parseur dérive de `prelev` (`Math.round(prelev / 500)`). Le montant et
 * le nombre d'enveloppes ne sont donc PAS la même grandeur, et un prélèvement
 * de moins de 250 € donne zéro enveloppe pour un montant non nul.
 */
const EUROS_PAR_ENVELOPPE = 500

/** Au-delà, ce n'est plus un prélèvement mais une donnée à ne pas croire. */
const PLAFOND_ENVELOPPES = 60

/** Chèques et bons divers : les seuls types qu'on saisit au montant, à la main. */
const TYPES_CLAVIER = new Set([2, 3, 14])
/** Tout ce qui porte un code-barres et se valide à la douchette Honeywell. */
const TYPES_SCAN = new Set([11, 12, 15, 16, 17, 24, 25, 26, 27, 30, 32, 33, 34, 35, 61])

const LIBELLES_TYPE: Record<number, { nom: string; court: string }> = {
  2:  { nom: 'CHQ AUTO',          court: 'CHQ' },
  3:  { nom: 'CHQ MANUEL',        court: 'CHQ' },
  11: { nom: 'BON FOURNISSEUR',   court: 'BON' },
  12: { nom: 'BON MAGASIN',       court: 'BON' },
  14: { nom: 'BON DIVERS',        court: 'BON' },
  17: { nom: 'BON CATALINA',      court: 'BON' },
  30: { nom: 'VAT',               court: 'VAT' },
  61: { nom: 'COUPON HIGHCO',     court: 'CPH' },
  15: { nom: 'TICKET RESTAURANT', court: 'TRP' },
  16: { nom: 'CAP',               court: 'CAP' },
  24: { nom: 'CAP EDENRED',       court: 'CAP' },
  25: { nom: 'CAP SODEXO',        court: 'CAP' },
  26: { nom: 'CAP UP',            court: 'CAP' },
  27: { nom: 'CAP BIMPLI',        court: 'CAP' },
  32: { nom: 'TRP EDENRED',       court: 'TRP' },
  33: { nom: 'TRP SODEXO',        court: 'TRP' },
  34: { nom: 'TRP UP',            court: 'TRP' },
  35: { nom: 'TRP BIMPLI',        court: 'TRP' },
}

// Le code couleur par famille vient de la V2 : c'est lui qui permet de lever
// une ambiguïté de montant d'un coup d'œil, sans lire l'étiquette.
const COULEURS_TYPE: Record<string, { fond: string; trait: string; encre: string }> = {
  CHQ: { fond: 'var(--fam-chq-w)', trait: 'var(--fam-chq-b)', encre: 'var(--fam-chq)' },
  BON: { fond: 'var(--fam-bon-w)', trait: 'var(--fam-bon-b)', encre: 'var(--fam-bon)' },
  VAT: { fond: 'var(--fam-vat-w)', trait: 'var(--fam-vat-b)', encre: 'var(--fam-vat)' },
  CPH: { fond: 'var(--fam-cph-w)', trait: 'var(--fam-cph-b)', encre: 'var(--fam-cph)' },
  TRP: { fond: 'var(--fam-trp-w)', trait: 'var(--fam-trp-b)', encre: 'var(--fam-trp)' },
  CAP: { fond: 'var(--fam-cap-w)', trait: 'var(--fam-cap-b)', encre: 'var(--fam-cap)' },
}

type Phase = 'caissiers' | 'scan' | 'resume' | 'fin'

interface Paiement {
  id: string
  montant: number
  reglementId: number | null
  typeReglement: string | null
  numeroOperateur: number | null
  operateur: string
  ticket: string
  heure: string
  caisse: string
}

interface OpRapport {
  id: string
  nom: string
  badge: string
  fdc: number
  reglEsp: number
  prelev: number
  prelevCount: number
  prelevIrregular: boolean
  decl: number
  diff: number
}

interface Operateur {
  num: number
  nom: string
  paiements: Paiement[]
  rapportOp: OpRapport | null
}

const objet = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}

const tableau = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? (v as Record<string, unknown>[]) : []

/** Les anciens jetons anglais sont encore en base sur les journées de 2025. */
const normStatut = (v: unknown): string | null =>
  v === 'confirmed' ? 'valide'
  : v === 'rejected' ? 'non_conforme'
  : typeof v === 'string' && v ? v
  : null

const nomCourt = (nom: string) => nom.replace(/\s*\(\d+\)\s*$/, '').trim()

/** Le montant signé, comme en V2 : un « + » explicite distingue l'encaissement du rendu. */
const signe = (m: number) => `${m >= 0 ? '+' : ''}${fmtEur(m)}`

const centsEnEuros = (saisie: string) => (parseInt(saisie, 10) || 0) / 100

/**
 * Ce qui reste du libellé une fois la famille retirée : « CAP SODEXO » → SODEXO.
 *
 * La teinte de famille ne peut pas porter seule la distinction : en peau
 * registre, --fam-bon (#7A5F32) et --fam-cap (#7A5A12) sont à deux doigts l'un
 * de l'autre. On met donc le code de famille TOUJOURS au même endroit, en
 * chasse fixe, sur trois lettres : l'œil se cale dessus, la couleur ne fait que
 * confirmer.
 */
const detailType = (nom: string, court: string): string =>
  nom.startsWith(court + ' ') ? nom.slice(court.length + 1) : nom === court ? '' : nom

/**
 * Les montants s'empilent en colonne le long d'une liste : chasse fixe, largeur
 * figée, alignés à droite. Sans largeur, la virgule se déplace d'une ligne à
 * l'autre et on relit chaque montant au lieu de balayer la colonne.
 */
const COL_MONTANT: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, minWidth: 92, textAlign: 'right', whiteSpace: 'nowrap',
}

/**
 * Une action qui attend le serveur porte `data-encours` : le CSS la rend inerte,
 * donc plus de double-clic possible pendant l'aller-retour. La garde `vivant`
 * évite d'écrire dans l'état après démontage — quitter le wizard pendant une
 * écriture est le cas normal, pas l'exception.
 */
function useEnCours() {
  const [cles, setCles] = useState<Set<string>>(new Set())
  const vivant = useRef(true)
  useEffect(() => { vivant.current = true; return () => { vivant.current = false } }, [])

  const lancer = useCallback(async (cle: string, action: () => Promise<unknown> | void) => {
    setCles((p) => new Set(p).add(cle))
    try { await action() } finally {
      if (vivant.current) {
        setCles((p) => { const s = new Set(p); s.delete(cle); return s })
      }
    }
  }, [])

  /** À poser sur le bouton : `data-encours={enCours('cle')}`. */
  const enCours = useCallback(
    (cle: string): 'true' | undefined => (cles.has(cle) ? 'true' : undefined),
    [cles],
  )
  return { enCours, lancer }
}

export default function DepouillementWizard({ rapport, lectureSeule, onQuitter }: {
  rapport: Rapport | null
  lectureSeule: boolean
  onQuitter: () => void
}) {
  const { date, ecrireRapport, recharger } = useRun()
  const { seuilDeclarationEspeces } = useReglages()

  const [phase, setPhase] = useState<Phase>('caissiers')
  const [indexOp, setIndexOp] = useState(0)
  const [saisie, setSaisie] = useState('')
  const [ambigu, setAmbigu] = useState<number[] | null>(null)
  const [plusArme, setPlusArme] = useState(false)
  const [erreurSaisie, setErreurSaisie] = useState(false)
  // Un refus silencieux (bord rouge, 380 ms) laissait deviner la cause. Debout
  // et vite, on ne devine pas : on redit le montant, on s'énerve. La raison est
  // désormais écrite sous le champ.
  const [messageErreur, setMessageErreur] = useState<string | null>(null)
  // Le refus parlait, la réussite se taisait : le champ se vidait, et c'était
  // tout. Or c'est là que se prennent les deux décisions du geste suivant —
  // QUEL type a été retenu (il est deviné, on ne l'a jamais choisi) et COMBIEN
  // il en reste dans l'enveloppe. Les yeux sont sur les chèques, pas sur la
  // liste : la ligne d'état les dit à voix haute.
  const [messageOk, setMessageOk] = useState<string | null>(null)
  const [ligneFlash, setLigneFlash] = useState<string | null>(null)
  const [replies, setReplies] = useState<Set<number>>(new Set())
  const [aide, setAide] = useState(false)

  const champRef = useRef<HTMLInputElement | null>(null)
  const minuteurErreur = useRef<number | undefined>(undefined)
  const minuteurFlash = useRef<number | undefined>(undefined)
  const minuteurPlus = useRef<number | undefined>(undefined)

  useEffect(() => () => {
    window.clearTimeout(minuteurErreur.current)
    window.clearTimeout(minuteurFlash.current)
    window.clearTimeout(minuteurPlus.current)
  }, [])

  // Le temps réel est déjà tenu par RunProvider (canal postgres_changes sur
  // `journees`) : une validation faite ailleurs arrive ici sans rien câbler.
  // Reste le rattrapage au retour d'onglet — la V2 le faisait par
  // router.refresh(), remplacé ici par le rechargement de la run. Sans lui, un
  // ré-import fait dans un autre onglet laisserait des identifiants périmés à
  // l'écran, et les validations partiraient sur des paiements qui n'existent
  // plus (symptôme « la liste glisse », audit BUG-3 du 22/05/2026).
  useEffect(() => {
    const surVisibilite = () => {
      if (document.visibilityState === 'visible') void recharger()
    }
    document.addEventListener('visibilitychange', surVisibilite)
    return () => document.removeEventListener('visibilitychange', surVisibilite)
  }, [recharger])

  // ── Ce que le rapport dit ────────────────────────────────────────────────

  const verifications = useMemo(() => objet(rapport?.verifications), [rapport])
  const paymentMeta = useMemo(() => objet(rapport?.paymentMeta), [rapport])
  const rapportState = useMemo(() => objet(rapport?.rapportState), [rapport])

  const paiements = useMemo<Paiement[]>(() => tableau(rapport?.payments).map((p) => ({
    id: String(p.id ?? ''),
    montant: Number(p.montant) || 0,
    reglementId: p.reglementId == null ? null : Number(p.reglementId),
    typeReglement: typeof p.typeReglement === 'string' ? p.typeReglement : null,
    numeroOperateur: p.numeroOperateur == null ? null : Number(p.numeroOperateur),
    operateur: String(p.operateur ?? ''),
    ticket: String(p.ticket ?? ''),
    heure: String(p.heure ?? ''),
    caisse: String(p.caisse ?? ''),
  })), [rapport])

  // `id` est lu tel quel, sans identifiant de repli : c'est la clé de
  // rapport.rapportState, et l'écran principal 05a la lit de la même façon
  // (`txt(o.id)`). Fabriquer un `op_0` ici ferait écrire les deux écrans à deux
  // endroits différents pour le même caissier.
  const opsRapport = useMemo<OpRapport[]>(() => tableau(rapport?.rapportOps).map((o) => ({
    id: o.id == null ? '' : String(o.id),
    nom: String(o.nom ?? ''),
    badge: String(o.badge ?? ''),
    fdc: Number(o.fdc) || 0,
    reglEsp: Number(o.reglEsp) || 0,
    prelev: Number(o.prelev) || 0,
    prelevCount: Number(o.prelevCount) || 0,
    prelevIrregular: o.prelevIrregular === true,
    decl: Number(o.decl) || 0,
    diff: Number(o.diff) || 0,
  })), [rapport])

  /** Les PAD sont hors de ce parcours : ils ont l'étape 03 et ses quatre issues. */
  const nonPad = useMemo(() => paiements.filter((p) => !isPadPayment(p)), [paiements])

  const statutDe = useCallback(
    (id: string) => normStatut(verifications[id]),
    [verifications],
  )

  // ── Les opérateurs du jour ───────────────────────────────────────────────
  // Reprise stricte du filtre V2 : un badge purement CB ou superviseur, sans
  // règlement, sans prélèvement et sans écart de caisse, n'a rien à dépouiller
  // — le faire défiler ferait perdre du temps à chaque journée.
  const operateurs = useMemo<Operateur[]>(() => {
    const groupes = new Map<number, { nom: string; paiements: Paiement[] }>()
    for (const p of nonPad) {
      const n = p.numeroOperateur
      if (n == null || !Number.isFinite(n)) continue
      const g = groupes.get(n) ?? { nom: p.operateur, paiements: [] }
      g.paiements.push(p)
      groupes.set(n, g)
    }

    const parBadge = new Map<number, OpRapport>()
    for (const o of opsRapport) {
      const n = parseInt(o.badge, 10)
      if (Number.isFinite(n)) parBadge.set(n, o)
    }

    const cles = new Set<number>([...groupes.keys(), ...parBadge.keys()])

    return [...cles]
      .filter((num) => {
        const pmts = groupes.get(num)?.paiements ?? []
        const op = parBadge.get(num)
        const aDesReglements = pmts.length > 0
        const aUnPrelevement = (op?.prelev ?? 0) > 0
        // Même epsilon que le module des écarts : en dessous, c'est un arrondi
        // de centime, pas un écart — et sûrement pas un caissier à dérouler.
        const aUnEcart = op != null && Math.abs(op.diff) >= EPSILON
        return aDesReglements || aUnPrelevement || aUnEcart
      })
      .sort((a, b) => a - b)
      .map((num) => {
        const g = groupes.get(num)
        const op = parBadge.get(num) ?? null
        return {
          num,
          nom: g?.nom || op?.nom || String(num),
          paiements: g?.paiements ?? [],
          rapportOp: op,
        }
      })
  }, [nonPad, opsRapport])

  const opCourant = useMemo(() => operateurs[indexOp], [operateurs, indexOp])

  const paiementsClavier = useMemo(
    () => (opCourant?.paiements ?? []).filter(
      (p) => p.reglementId != null && TYPES_CLAVIER.has(p.reglementId),
    ),
    [opCourant],
  )

  // Trié par code de règlement : la V2 groupait dans un objet, dont les clés
  // entières s'énumèrent en ordre croissant. Les groupes tombaient donc dans
  // l'ordre CHQ AUTO (02), CHQ MANUEL (03), BON DIVERS (14) — un ordre stable
  // d'un caissier à l'autre, sur lequel l'œil se repose. Une Map en ordre
  // d'arrivée des paiements ne l'aurait pas.
  const groupesClavier = useMemo(() => {
    const g = new Map<number, Paiement[]>()
    for (const p of paiementsClavier) {
      if (p.reglementId == null) continue
      const liste = g.get(p.reglementId) ?? []
      liste.push(p)
      g.set(p.reglementId, liste)
    }
    return [...g.entries()].sort((a, b) => a[0] - b[0])
  }, [paiementsClavier])

  // ── Les écritures ────────────────────────────────────────────────────────

  /**
   * Un lot de règlements passe en une seule écriture optimiste : le patch local
   * porte tout de suite les N statuts, et les RPC partent en série derrière. Un
   * appel par ligne partant d'un `rapport` non encore rafraîchi écraserait les
   * précédents — c'est exactement ce que fait « + + » sur un caissier.
   */
  const marquer = useCallback(async (ids: string[], statut: 'valide' | 'non_conforme') => {
    if (lectureSeule || ids.length === 0) return
    const suivant: Record<string, unknown> = { ...verifications }
    for (const id of ids) suivant[id] = statut
    try {
      await ecrireRapport(
        `dépouillement clavier — ${ids.length} règlement${ids.length > 1 ? 's' : ''}`,
        { verifications: suivant },
        async () => { for (const id of ids) await api.enregistrerVerification(date, id, statut) },
        ETAPE,
      )
    } catch { /* ecrireRapport a remis l'état d'avant et dit pourquoi */ }
  }, [date, ecrireRapport, lectureSeule, verifications])

  const decider = useCallback(async (id: string, inclure: boolean) => {
    if (lectureSeule) return
    const suivant = { ...paymentMeta, [id]: { ...objet(paymentMeta[id]), addToRemise: inclure } }
    try {
      await ecrireRapport(
        'décision de remise',
        { paymentMeta: suivant },
        () => api.patcherRapport(date, { paymentMeta: suivant }),
        ETAPE,
      )
    } catch { /* idem */ }
  }, [date, ecrireRapport, lectureSeule, paymentMeta])

  const majEnveloppe = useCallback(async (
    opId: string, rang: number, champ: string, valeur: boolean | undefined,
  ) => {
    if (lectureSeule) return
    const etatOp = objet(rapportState[opId])
    const cle = `prelev_${rang}`
    const suivant = {
      ...rapportState,
      [opId]: { ...etatOp, [cle]: { ...objet(etatOp[cle]), [champ]: valeur } },
    }
    try {
      await ecrireRapport(
        'contrôle d’enveloppe de prélèvement',
        { rapportState: suivant },
        () => api.patcherRapport(date, { rapportState: suivant }),
        ETAPE,
      )
    } catch { /* idem */ }
  }, [date, ecrireRapport, lectureSeule, rapportState])

  // ── La saisie au clavier ─────────────────────────────────────────────────

  const reinitSaisie = useCallback(() => {
    setSaisie('')
    setAmbigu(null)
    setPlusArme(false)
    window.clearTimeout(minuteurPlus.current)
    window.clearTimeout(minuteurErreur.current)
    setErreurSaisie(false)
    setMessageErreur(null)
    setMessageOk(null)
  }, [])

  const refuser = useCallback((raison: string) => {
    setErreurSaisie(true)
    setMessageErreur(raison)
    window.clearTimeout(minuteurErreur.current)
    minuteurErreur.current = window.setTimeout(() => setErreurSaisie(false), 380)
  }, [])

  /** Toute frappe efface le refus précédent : le message ne survit pas à sa cause. */
  const ecrireSaisie = useCallback((v: string) => {
    setMessageErreur(null)
    setSaisie(v)
  }, [])

  const flasher = useCallback((id: string) => {
    setLigneFlash(id)
    window.clearTimeout(minuteurFlash.current)
    minuteurFlash.current = window.setTimeout(() => setLigneFlash(null), 600)
  }, [])

  /**
   * Ce que la réussite doit dire, et qu'elle taisait : le type retenu — il est
   * DEVINÉ à partir du montant, on ne l'a jamais choisi — puis ce qui reste au
   * clavier chez ce caissier, et, à zéro, le geste suivant. À la dernière fiche
   * du dernier caissier, « → » est désactivé : envoyer quelqu'un l'essayer
   * serait exactement la seconde perdue qu'on cherche à éviter.
   */
  const confirmer = useCallback((p: Paiement) => {
    const nom = p.reglementId == null ? undefined : LIBELLES_TYPE[p.reglementId]?.nom
    const autres = paiementsClavier.filter((x) => x.id !== p.id)
    // « Reste » a le même sens que le compteur du bouton « Solder » : ce qui
    // n'a pas encore d'issue. Un non conforme est décidé — on ne le cherche
    // plus dans l'enveloppe — mais il se dit, sinon « caissier terminé » se
    // lirait « caissier conforme ».
    const reste = autres.filter((x) => {
      const s = statutDe(x.id)
      return s !== 'valide' && s !== 'non_conforme'
    }).length
    const nc = autres.filter((x) => statutDe(x.id) === 'non_conforme').length
    const ou = indexOp >= operateurs.length - 1
      ? 'dernier caissier, passe à la phase 2 (le scan)'
      : 'caissier terminé, → pour le suivant'
    const suite = reste > 0
      ? `reste ${reste} à trouver`
      : `${nc > 0 ? `${nc} non conforme${nc > 1 ? 's' : ''} — ` : ''}${ou}`
    setMessageOk(`${fmtEur(p.montant)} validé${nom ? ` — ${nom}` : ''} · ${suite}`)
  }, [indexOp, operateurs.length, paiementsClavier, statutDe])

  const validerMontant = useCallback(() => {
    if (lectureSeule) return
    const cents = parseInt(saisie, 10)
    if (isNaN(cents) || cents <= 0) {
      refuser('Saisis un montant en centimes, puis Entrée — 2234 vaut 22,34 €.')
      return
    }
    const montant = cents / 100
    const candidats = paiementsClavier.filter(
      (p) => Math.abs(p.montant - montant) < EPSILON && statutDe(p.id) !== 'valide',
    )
    if (candidats.length === 0) {
      // « Rien à ce montant » et « déjà validé » demandent deux gestes opposés :
      // rouvrir l'enveloppe, ou passer au suivant. Le refus doit les séparer.
      const dejaFait = paiementsClavier.some(
        (p) => Math.abs(p.montant - montant) < EPSILON && statutDe(p.id) === 'valide',
      )
      refuser(dejaFait
        ? `${fmtEur(montant)} : ce règlement est déjà validé pour ce caissier.`
        : `Aucun règlement clavier à ${fmtEur(montant)} en attente chez ce caissier.`)
      return
    }
    const types = [...new Set(candidats.map((p) => p.reglementId).filter((r): r is number => r != null))]
    const premier = candidats[0]
    if (types.length === 1 && premier) {
      void marquer([premier.id], 'valide')
      flasher(premier.id)
      reinitSaisie()
      confirmer(premier)
      return
    }
    setAmbigu(types)
  }, [confirmer, flasher, lectureSeule, marquer, paiementsClavier, refuser, reinitSaisie, saisie,
      statutDe])

  const leverAmbiguite = useCallback((reglementId: number) => {
    if (lectureSeule) return
    const montant = centsEnEuros(saisie)
    const trouve = paiementsClavier.find(
      (p) => p.reglementId === reglementId
        && Math.abs(p.montant - montant) < EPSILON
        && statutDe(p.id) !== 'valide',
    )
    if (!trouve) {
      refuser(`Plus aucun règlement de ce type à ${fmtEur(montant)} chez ce caissier.`)
      return
    }
    void marquer([trouve.id], 'valide')
    flasher(trouve.id)
    reinitSaisie()
    confirmer(trouve)
  }, [confirmer, flasher, lectureSeule, marquer, paiementsClavier, refuser, reinitSaisie, saisie,
      statutDe])

  /**
   * Destructif, et c'est voulu : « caissier suivant » veut dire « tout ce qui
   * reste n'a pas été trouvé dans l'enveloppe ». D'où le double appui sur « + »
   * — la barre passe à l'ambre au premier, l'action part au second.
   */
  const soldeEtAvance = useCallback(() => {
    if (lectureSeule) return
    // Ne réécrit QUE les indécis. Re-marquer non conforme ce qui l'est déjà,
    // c'est une RPC par ligne pour un état identique — et un compteur qui ment
    // sur le bouton (« 4 non conformes » quand trois le sont depuis un quart
    // d'heure). 'en_attente' et l'absence de statut, eux, sont bien soldés.
    const aSolder = paiementsClavier.filter((p) => {
      const s = statutDe(p.id)
      return s !== 'valide' && s !== 'non_conforme'
    })
    void marquer(aSolder.map((p) => p.id), 'non_conforme')
    const nom = opCourant ? nomCourt(opCourant.nom) : 'Caissier'
    if (indexOp >= operateurs.length - 1) setPhase('scan')
    else setIndexOp((i) => i + 1)
    reinitSaisie()
    // Le caissier soldé quitte l'écran à l'instant où on le solde : sans ce
    // mot, le seul témoin du geste destructif est une pastille ambre déjà
    // passée derrière. On dit qui, et combien.
    setMessageOk(`${nom} soldé · ${aSolder.length > 0
      ? `${aSolder.length} non conforme${aSolder.length > 1 ? 's' : ''}`
      : 'rien à marquer'}`)
  }, [indexOp, lectureSeule, marquer, opCourant, operateurs.length, paiementsClavier,
      reinitSaisie, statutDe])

  /**
   * Le geste d'armement, partagé par le clavier et la souris — une seule
   * mécanique, donc un seul comportement à connaître. La fenêtre diffère parce
   * que le geste diffère : 500 ms c'est un « + + » au pavé numérique, ce n'est
   * pas un double clic sur un bouton dont le libellé change entre les deux.
   */
  const demanderSolde = useCallback((fenetreMs: number) => {
    if (lectureSeule) return
    window.clearTimeout(minuteurPlus.current)
    if (plusArme) { setPlusArme(false); soldeEtAvance(); return }
    setPlusArme(true)
    minuteurPlus.current = window.setTimeout(() => setPlusArme(false), fenetreMs)
  }, [lectureSeule, plusArme, soldeEtAvance])

  const soldeALaSouris = useCallback(() => demanderSolde(2500), [demanderSolde])

  /** Navigation pure — aucune écriture, contrairement à « + + ». */
  const allerAuCaissier = useCallback((i: number) => {
    if (i < 0 || i >= operateurs.length) return
    setIndexOp(i)
    reinitSaisie()
  }, [operateurs.length, reinitSaisie])

  // Écoute globale : les raccourcis répondent même quand le focus est sur une
  // case du panneau rapport — sinon cocher une languette ferait perdre le
  // clavier pour le reste du caissier.
  useEffect(() => {
    if (phase !== 'caissiers' || lectureSeule || aide) return
    const surTouche = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const actif = document.activeElement
      const autreChamp = actif != null
        && ['INPUT', 'TEXTAREA'].includes(actif.tagName)
        && actif !== champRef.current
      // Entrée sur un bouton, c'est le BOUTON qu'on veut. Sans cette garde, le
      // `preventDefault` de la validation mangeait l'activation native :
      // « Manquante », les ✓ / ✗ et « Solder le reste » ne répondaient plus
      // qu'à la barre d'espace — ce que rien à l'écran ne dit.
      const surUnBouton = actif != null && (actif.tagName === 'BUTTON' || actif.tagName === 'A')

      if (e.key === '+' && !autreChamp) {
        e.preventDefault()
        demanderSolde(500)
      } else if (ambigu) {
        if (e.key === 'Escape') { e.preventDefault(); setAmbigu(null) }
        else if (/^[1-9]$/.test(e.key)) {
          const i = parseInt(e.key, 10) - 1
          const choix = ambigu[i]
          if (choix != null) { e.preventDefault(); leverAmbiguite(choix) }
        }
      } else if (e.key === 'Enter') {
        if (autreChamp || surUnBouton) return
        e.preventDefault()
        validerMontant()
      } else if (e.key === 'Escape') {
        if (autreChamp) return
        e.preventDefault()
        // Échap se dégonfle par couches, de la plus locale à la plus large : il
        // désarme, puis il efface, et seulement sur un écran déjà propre il
        // rend la main. Quitter ne perd rien — tout est écrit au fil de l'eau —
        // mais ne doit jamais arriver par surprise sur une frappe en cours.
        if (plusArme) { window.clearTimeout(minuteurPlus.current); setPlusArme(false) }
        else if (saisie || messageErreur) reinitSaisie()
        else onQuitter()
      } else if (e.key === '?') {
        if (autreChamp) return
        e.preventDefault()
        setAide(true)
      } else if (e.key === 'ArrowLeft' && !saisie) {
        if (autreChamp) return
        e.preventDefault()
        allerAuCaissier(indexOp - 1)
      } else if (e.key === 'ArrowRight' && !saisie) {
        if (autreChamp) return
        e.preventDefault()
        allerAuCaissier(indexOp + 1)
      } else if (/^[0-9]$/.test(e.key) && actif !== champRef.current && !autreChamp) {
        e.preventDefault()
        setMessageErreur(null)
        setSaisie((v) => v + e.key)
        champRef.current?.focus()
      } else if (e.key === 'Backspace' && actif !== champRef.current && !autreChamp) {
        e.preventDefault()
        setMessageErreur(null)
        setSaisie((v) => v.slice(0, -1))
        champRef.current?.focus()
      }
    }
    document.addEventListener('keydown', surTouche)
    return () => document.removeEventListener('keydown', surTouche)
  }, [aide, allerAuCaissier, ambigu, demanderSolde, indexOp, leverAmbiguite, lectureSeule,
      messageErreur, onQuitter, phase, plusArme, reinitSaisie, saisie, validerMontant])

  // Le focus doit revenir au champ à CHAQUE fois qu'un voile se lève : changer
  // de caissier, sortir du choix de type (la barre démonte le champ pour poser
  // les boutons, `champRef` tombe à null), refermer les raccourcis. Sans
  // `ambigu` et `aide` ici, on se retrouvait à taper sur un champ qui n'avait
  // plus le focus — la frappe marchait encore (l'écoute est globale) mais le
  // curseur avait disparu, et plus rien ne disait où l'on écrivait.
  useEffect(() => {
    if (phase === 'caissiers' && !ambigu && !aide) champRef.current?.focus()
  }, [phase, indexOp, ambigu, aide])

  // Une confirmation ne parle que de la frappe qui vient d'avoir lieu. Passer
  // au scan puis revenir aux caissiers ne doit pas la retrouver intacte : elle
  // annoncerait un « reste » vieux de dix minutes, et sur cet écran le temps
  // réel travaille pendant qu'on est ailleurs.
  useEffect(() => { setMessageOk(null) }, [phase])

  // Un ré-import fait ailleurs peut raccourcir la liste sous les pieds (le
  // rechargement au retour d'onglet ci-dessus, audit BUG-3). Sans ce recadrage
  // l'index resterait au-delà du dernier caissier : la plaque de nom et les
  // flèches vivent dans `opCourant &&` et disparaîtraient toutes les trois,
  // ne laissant que les pastilles pour s'en sortir.
  useEffect(() => {
    if (operateurs.length > 0 && indexOp >= operateurs.length) setIndexOp(operateurs.length - 1)
  }, [operateurs.length, indexOp])

  // ── Phase 2 · le scan ────────────────────────────────────────────────────

  const opsScan = useMemo(
    () => operateurs
      .map((op) => ({
        ...op,
        aScanner: op.paiements.filter((p) => p.reglementId != null && TYPES_SCAN.has(p.reglementId)),
      }))
      .filter((op) => op.aScanner.length > 0),
    [operateurs],
  )

  // Un opérateur entièrement scanné se replie tout seul : ce qui reste à faire
  // remonte visuellement, sans clic.
  useEffect(() => {
    if (phase !== 'scan') return
    setReplies((prev) => {
      const suite = new Set(prev)
      for (const op of opsScan) {
        if (op.aScanner.every((p) => normStatut(verifications[p.id]) === 'valide')) suite.add(op.num)
      }
      // Rendre `prev` quand rien n'a bougé : sinon chaque passage crée un Set
      // neuf, donc un rendu de plus à chaque validation reçue en temps réel.
      return suite.size === prev.size ? prev : suite
    })
  }, [phase, opsScan, verifications])

  const basculerRepli = (num: number) => setReplies((prev) => {
    const suite = new Set(prev)
    if (suite.has(num)) suite.delete(num); else suite.add(num)
    return suite
  })

  const statsScan = useMemo(() => {
    let total = 0
    let scannes = 0
    for (const op of opsScan) {
      for (const p of op.aScanner) {
        total++
        if (statutDe(p.id) === 'valide') scannes++
      }
    }
    return { total, scannes }
  }, [opsScan, statutDe])

  // ── Phase 3 · les décisions de remise ────────────────────────────────────

  const resumeParOp = useMemo(
    () => operateurs
      .map((op) => ({ ...op, manquants: op.paiements.filter((p) => statutDe(p.id) !== 'valide') }))
      .filter((op) => op.manquants.length > 0),
    [operateurs, statutDe],
  )

  const resumeTotal = resumeParOp.reduce((s, op) => s + op.manquants.length, 0)
  const resumeADecider = useMemo(
    () => resumeParOp.reduce((s, op) => s + op.manquants.filter(
      (p) => objet(paymentMeta[p.id]).addToRemise === undefined,
    ).length, 0),
    [resumeParOp, paymentMeta],
  )

  // ── Phase 4 · le compte final, en seaux qui ne se recouvrent pas ─────────

  const statsFin = useMemo(() => {
    let valides = 0
    let inclus = 0
    let exclus = 0
    let aDecider = 0
    for (const p of nonPad) {
      if (statutDe(p.id) === 'valide') { valides++; continue }
      const d = objet(paymentMeta[p.id]).addToRemise
      if (d === false) exclus++
      else if (d === true) inclus++
      else aDecider++
    }
    return { valides, inclus, exclus, aDecider, total: nonPad.length }
  }, [nonPad, paymentMeta, statutDe])

  // ── Rendu ────────────────────────────────────────────────────────────────

  const entete = (
    <Notice
      c="act"
      action={
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button className="btn gh sm" onClick={() => setAide(true)}
                  title="Ouvrir la liste des raccourcis (touche ?)">
            Raccourcis <span className="mo hint">?</span>
          </button>
          <button className="btn sm" onClick={onQuitter}>↩ Revenir au dépouillement</button>
        </div>
      }
    >
      <b>Wizard clavier</b> — mode alternatif de la branche 05a, à côté de l’écran principal et
      jamais à sa place. Les deux écrivent au même endroit : tu peux passer de l’un à l’autre en
      cours de dépouillement.
    </Notice>
  )

  if (paiements.length === 0 && opsRapport.length === 0) {
    return (
      <>
        {entete}
        <Vide>
          Aucune donnée importée. Le wizard a besoin du suivi des règlements et du rapport
          opérateur — reviens à l’étape 01 Import.
        </Vide>
      </>
    )
  }

  return (
    <>
      {entete}

      {/* Sélecteur de phase : quatre états, un seul retenu. C'est `aria-pressed`
          qui le dit — pas `.pri`, qui désignerait quatre actions principales
          dans la même zone et ne dirait plus rien. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }} role="group"
           aria-label="Phase du wizard">
        {([
          ['caissiers', '1 · Caissiers'],
          ['scan', '2 · Scan TRP / CAP'],
          ['resume', '3 · Décisions de remise'],
          ['fin', '4 · Fin de session'],
        ] as Array<[Phase, string]>).map(([p, nom]) => (
          <button key={p} className="btn sm" aria-pressed={phase === p} onClick={() => setPhase(p)}>
            {nom}
          </button>
        ))}
      </div>

      {phase === 'caissiers' && (
        <PhaseCaissiers
          operateurs={operateurs}
          indexOp={indexOp}
          opCourant={opCourant}
          groupesClavier={groupesClavier}
          paiementsClavier={paiementsClavier}
          statutDe={statutDe}
          ligneFlash={ligneFlash}
          ambigu={ambigu}
          saisie={saisie}
          erreurSaisie={erreurSaisie}
          messageErreur={messageErreur}
          messageOk={messageOk}
          plusArme={plusArme}
          lectureSeule={lectureSeule}
          champRef={champRef}
          rapportState={rapportState}
          seuil={seuilDeclarationEspeces}
          aDesOpsRapport={opsRapport.length > 0}
          setSaisie={ecrireSaisie}
          setAmbigu={setAmbigu}
          allerAuCaissier={allerAuCaissier}
          leverAmbiguite={leverAmbiguite}
          majEnveloppe={majEnveloppe}
          demanderSolde={soldeALaSouris}
          allerAuScan={() => setPhase('scan')}
        />
      )}

      {phase === 'scan' && (
        <PhaseScan
          opsScan={opsScan}
          replies={replies}
          statutDe={statutDe}
          stats={statsScan}
          lectureSeule={lectureSeule}
          basculerRepli={basculerRepli}
          valider={(id) => marquer([id], 'valide')}
          revenir={() => setPhase('caissiers')}
          continuer={() => setPhase('resume')}
        />
      )}

      {phase === 'resume' && (
        <PhaseResume
          resumeParOp={resumeParOp}
          total={resumeTotal}
          aDecider={resumeADecider}
          paymentMeta={paymentMeta}
          lectureSeule={lectureSeule}
          decider={decider}
          revenir={() => setPhase('scan')}
          continuer={() => setPhase('fin')}
        />
      )}

      {phase === 'fin' && (
        <PhaseFin
          stats={statsFin}
          recommencer={() => { setPhase('caissiers'); setIndexOp(0); reinitSaisie() }}
          quitter={onQuitter}
        />
      )}

      {aide && <Aide fermer={() => setAide(false)} />}
    </>
  )
}

// ── Phase 1 · les caissiers au clavier ──────────────────────────────────────

function PhaseCaissiers({
  operateurs, indexOp, opCourant, groupesClavier, paiementsClavier, statutDe, ligneFlash,
  ambigu, saisie, erreurSaisie, messageErreur, messageOk, plusArme, lectureSeule, champRef,
  rapportState, seuil, aDesOpsRapport, setSaisie, setAmbigu, allerAuCaissier, leverAmbiguite,
  majEnveloppe, demanderSolde, allerAuScan,
}: {
  operateurs: Operateur[]
  indexOp: number
  opCourant: Operateur | undefined
  groupesClavier: Array<[number, Paiement[]]>
  paiementsClavier: Paiement[]
  statutDe: (id: string) => string | null
  ligneFlash: string | null
  ambigu: number[] | null
  saisie: string
  erreurSaisie: boolean
  messageErreur: string | null
  messageOk: string | null
  plusArme: boolean
  lectureSeule: boolean
  champRef: React.MutableRefObject<HTMLInputElement | null>
  rapportState: Record<string, unknown>
  seuil: number
  aDesOpsRapport: boolean
  setSaisie: (v: string) => void
  setAmbigu: (v: number[] | null) => void
  allerAuCaissier: (i: number) => void
  leverAmbiguite: (reglementId: number) => void
  majEnveloppe: (opId: string, rang: number, champ: string, valeur: boolean | undefined) => Promise<void>
  demanderSolde: () => void
  allerAuScan: () => void
}) {
  const valides = paiementsClavier.filter((p) => statutDe(p.id) === 'valide').length
  // Ce que « + + » écrirait vraiment : ni les validés, ni les déjà non
  // conformes. Le bouton annonce donc un nombre qu'il tiendra.
  const aSolder = paiementsClavier.filter((p) => {
    const s = statutDe(p.id)
    return s !== 'valide' && s !== 'non_conforme'
  }).length

  /** Trois états par caissier, comme en V2 : fait, incomplet, rien. */
  const etatPastille = (op: Operateur, i: number): '' | 'cur' | 'done' | 'reste' => {
    if (i === indexOp) return 'cur'
    const clavier = op.paiements.filter((p) => p.reglementId != null && TYPES_CLAVIER.has(p.reglementId))
    if (clavier.length === 0) return ''
    const tousFaits = clavier.every((p) => {
      const s = statutDe(p.id)
      return s === 'valide' || s === 'non_conforme'
    })
    if (clavier.some((p) => statutDe(p.id) === 'non_conforme')) return 'reste'
    return tousFaits ? 'done' : ''
  }

  // Jamais de cul-de-sac : sans caissier retenu, l'écran n'affichait ni plaque,
  // ni flèches, ni pastilles — une barre de saisie seule au milieu de rien.
  if (operateurs.length === 0) {
    return (
      <Vide>
        <div style={{ marginBottom: 12 }}>
          Aucun caissier à dépouiller au clavier : personne n’a de règlement, de prélèvement
          ni d’écart de caisse sur cette journée.
        </div>
        <button className="btn pri" onClick={allerAuScan}>Passer au scan TRP / CAP</button>
      </Vide>
    )
  }

  return (
    <>
      {opCourant && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <button className="btn" disabled={indexOp === 0} title="Caissier précédent (flèche gauche)"
                  aria-label="Aller au caissier précédent"
                  onClick={() => allerAuCaissier(indexOp - 1)}>←</button>
          <div style={{
            flex: 1, background: 'var(--ink)', color: 'var(--on-nuit)', padding: '10px 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <div style={{ minWidth: 0 }}>
              <div className="mo" style={{ fontSize: 9.5, letterSpacing: '.1em', color: 'var(--on-nuit-mute)' }}>
                {isUnbadgedOperateur(opCourant.num)
                  ? <span style={{ color: 'var(--warn-b)' }}>⚠ BADGE MANQUANT DANS LE CSV</span>
                  : `#${opCourant.num}`}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-.01em' }}>
                {nomCourt(opCourant.nom)}
              </div>
            </div>
            <div className="mo" style={{ fontSize: 10, color: 'var(--on-nuit-mute)', textAlign: 'right', letterSpacing: '.06em' }}>
              <div>{indexOp + 1} / {operateurs.length}</div>
              <div style={{ color: 'var(--on-nuit)' }}>{valides} / {paiementsClavier.length} VALIDÉS</div>
            </div>
          </div>
          <button className="btn" disabled={indexOp >= operateurs.length - 1}
                  title="Caissier suivant, sans rien marquer (flèche droite)"
                  aria-label="Aller au caissier suivant, sans rien marquer"
                  onClick={() => allerAuCaissier(indexOp + 1)}>→</button>
        </div>
      )}

      {/* Les pastilles gardent leur trait de 11 px — c'est une frise, pas une
          rangée de boutons — mais la cible de clic monte à 21 px de haut grâce
          au remplissage : `background-clip: content-box` ne peint que le trait.
          Onze passages par jour, debout, on ne vise pas une barre de 11 px. */}
      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginBottom: 12 }} role="group"
           aria-label="Caissiers de la journée">
        {operateurs.map((op, i) => {
          const e = etatPastille(op, i)
          const etatMot = e === 'cur' ? 'en cours'
            : e === 'done' ? 'dépouillé'
            : e === 'reste' ? 'reste des non conformes' : 'pas encore dépouillé'
          return (
            <button
              key={op.num}
              onClick={() => allerAuCaissier(i)}
              title={`${nomCourt(op.nom)} (#${op.num}) — ${etatMot}`}
              aria-label={`Aller au caissier ${nomCourt(op.nom)} — ${etatMot}`}
              aria-current={e === 'cur' ? 'true' : undefined}
              style={{
                width: e === 'cur' ? 26 : 11, height: 21, padding: '5px 0', border: 'none',
                backgroundClip: 'content-box',
                background: e === 'cur' ? 'var(--act)'
                  : e === 'done' ? 'var(--ok)'
                  : e === 'reste' ? 'var(--warn)' : 'var(--line2)',
              }}
            />
          )
        })}
      </div>

      {/* La barre de saisie colle EN HAUT, sous le bandeau de run : le bas de
          l'écran appartient à la barre d'action de l'enveloppe (§4.4), qui
          porte « Valider l'étape » — deux barres collantes en bas se
          recouvriraient. */}
      <div style={{
        position: 'sticky', top: 42, zIndex: 12, marginBottom: 12,
        background: plusArme ? 'var(--warn-w)' : 'var(--surface)',
        border: `1px solid ${plusArme ? 'var(--warn-b)' : 'var(--line2)'}`,
        padding: '9px 12px',
      }}>
        {ambigu ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="mo big">{fmtEur(centsEnEuros(saisie))}</span>
            <span className="hint">—</span>
            {ambigu.map((rid, i) => {
              const info = LIBELLES_TYPE[rid]
              const c = info ? COULEURS_TYPE[info.court] : undefined
              return (
                <button key={rid} className="btn sm" onClick={() => leverAmbiguite(rid)}
                        title={`Choisir ${info?.nom ?? `le type ${rid}`} — touche ${i + 1}`}
                        style={c ? { background: c.fond, borderColor: c.trait, color: c.encre } : undefined}>
                  <span className="mo">[{i + 1}]</span> {info?.nom ?? `Type ${rid}`}
                </button>
              )
            })}
            <span className="sp" />
            <button className="btn gh sm" onClick={() => setAmbigu(null)}>Annuler le choix (Échap)</button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="hint">›</span>
            <input
              ref={champRef}
              className="mo"
              type="text"
              inputMode="numeric"
              value={saisie}
              disabled={lectureSeule}
              aria-label="Montant en centimes"
              aria-invalid={erreurSaisie || undefined}
              placeholder="montant en centimes (ex : 2234 = 22,34 €)"
              onChange={(e) => setSaisie(e.target.value.replace(/[^0-9]/g, ''))}
              style={{
                flex: 1, padding: '7px 10px', fontSize: 14,
                border: `1px solid ${erreurSaisie ? 'var(--stop)' : 'var(--line2)'}`,
                background: erreurSaisie ? 'var(--stop-w)' : 'var(--surface2)',
              }}
            />
            {saisie && (
              <span className="mo" style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>
                = {fmtEur(centsEnEuros(saisie))}
              </span>
            )}
            <span className="hint">↵</span>
            {/* « + + » n'existait qu'au clavier : le seul geste destructif de
                l'écran était invisible pour qui tient la souris. Même mécanique
                d'armement, donc même comportement à connaître — et le libellé
                dit ce que le second appui va marquer. */}
            <button
              className="btn stop sm"
              disabled={lectureSeule || aSolder === 0}
              aria-pressed={plusArme}
              onClick={demanderSolde}
              title={aSolder === 0
                ? 'Rien à solder : chaque règlement clavier de ce caissier a déjà son issue, validé ou non conforme. Utilise → pour passer au suivant.'
                : `Marque non conformes les ${aSolder} règlement(s) clavier non trouvés chez ce caissier, puis passe au suivant. Deux appuis : le premier arme, le second exécute. Raccourci « + + ».`}
            >
              {plusArme
                ? `Confirmer · ${aSolder} non conforme${aSolder > 1 ? 's' : ''}`
                : 'Solder le reste'}
            </button>
          </div>
        )}
        {/* La ligne d'état, par ordre d'urgence : le refus, le choix en cours,
            l'armement, puis — le champ une fois vide — ce que la dernière
            frappe a fait. Dès qu'on retape, la confirmation s'efface d'elle
            même : elle ne parle que du geste terminé. Ce vert-là dit VALIDÉ,
            pas « écart nul ». */}
        <div className="hint" role="status" aria-live="polite"
             style={{
               marginTop: 5, color: plusArme ? 'var(--warn)' : undefined,
               fontWeight: plusArme || messageErreur || (messageOk && !saisie) ? 600 : 400,
             }}>
          {messageErreur
            ? <span style={{ color: 'var(--stop)' }}>{messageErreur}</span>
            : ambigu
            ? 'Choisis le type · 1, 2, 3… ou Échap pour annuler'
            : plusArme
            ? '+ + appuie encore sur + pour marquer le reste non conforme et passer au caissier suivant'
            : messageOk && !saisie
            ? <span style={{ color: 'var(--ok)' }}>✓ {messageOk}</span>
            : 'Entrée valide · + + solde le caissier · ← → navigation · ? raccourcis · Échap efface, puis quitte'}
        </div>
      </div>

      <div className="two">
        <Card titre="Règlements à saisir au clavier — chèques et bons divers"
              lampe={paiementsClavier.length > 0 && valides === paiementsClavier.length ? 'ok' : 'act'}>
          {groupesClavier.length === 0
            ? <Vide>Aucun règlement clavier pour ce caissier.</Vide>
            : groupesClavier.map(([code, pmts]) => {
              const surligne = ambigu?.includes(code) ?? false
              const attenue = ambigu != null && !ambigu.includes(code)
              const totalGroupe = pmts.reduce((s, p) => s + p.montant, 0)
              return (
                <div key={code} style={{ opacity: attenue ? 0.4 : 1, background: surligne ? 'var(--act-w)' : undefined }}>
                  <div className="slot" style={{ background: 'var(--surface2)' }}>
                    <BadgeType reglementId={code} />
                    <span className="mo hint">code {String(code).padStart(2, '0')}</span>
                    <span className="sp" />
                    <span className="mo" style={{ fontSize: 11.5, color: 'var(--ink3)' }}>
                      {pmts.length} · {fmtEur(totalGroupe)}
                    </span>
                  </div>
                  {pmts.map((p) => {
                    const st = statutDe(p.id)
                    const flash = ligneFlash === p.id
                    return (
                      <div className="slot" key={p.id} style={{
                        boxShadow: `inset 3px 0 0 ${st === 'valide' ? 'var(--ok)' : st === 'non_conforme' ? 'var(--warn)' : 'transparent'}`,
                        background: flash ? 'var(--ok-w)'
                          : st === 'valide' ? 'var(--ok-w)'
                          : st === 'non_conforme' ? 'var(--warn-w)' : undefined,
                      }}>
                        <span className="mo" style={{ fontSize: 11.5, color: 'var(--ink3)' }}>#{p.ticket}</span>
                        <span className="hint">{p.heure}</span>
                        <span className="hint">Caisse {p.caisse}</span>
                        <span className="sp" />
                        <span className="mo" style={COL_MONTANT}>{signe(p.montant)}</span>
                        {st === 'valide' && <Chip c="ok">VALIDÉ</Chip>}
                        {st === 'non_conforme' && <Chip c="warn">NON CONFORME</Chip>}
                      </div>
                    )
                  })}
                </div>
              )
            })}
        </Card>

        {aDesOpsRapport && (
          <Card titre="Rapport opérateur" lampe={opCourant?.rapportOp ? 'act' : 'warn'}>
            {/* `key` par caissier : changer de caissier remonte le panneau, ce
                qui remet à zéro les boutons restés « en cours » d'un envoi
                appartenant au caissier d'avant. */}
            {opCourant?.rapportOp
              ? <PanneauRapport key={opCourant.num} op={opCourant.rapportOp}
                                rapportState={rapportState} seuil={seuil}
                                lectureSeule={lectureSeule} majEnveloppe={majEnveloppe} />
              : <Vide>Ce caissier n’est pas dans le rapport opérateur.</Vide>}
          </Card>
        )}
      </div>
    </>
  )
}

// ── Le panneau rapport opérateur, repris de la V2 ───────────────────────────

function PanneauRapport({ op, rapportState, seuil, lectureSeule, majEnveloppe }: {
  op: OpRapport
  rapportState: Record<string, unknown>
  seuil: number
  lectureSeule: boolean
  majEnveloppe: (opId: string, rang: number, champ: string, valeur: boolean | undefined) => Promise<void>
}) {
  const { enCours, lancer } = useEnCours()
  const etat = objet(rapportState[op.id])
  const auDessus = op.decl > seuil

  // `op.id` est la CLÉ de rapport.rapportState. Vide, tous les opérateurs sans
  // identifiant partageraient la même entrée `rapportState['']` : cocher la
  // languette de l'un cocherait celle des autres. Le parseur produit toujours
  // un id (`op_<badge>` ou un hachage de contenu), mais un vieux blob peut ne
  // pas en avoir — on ferme la saisie plutôt que d'écrire au mauvais endroit.
  const sansIdentifiant = op.id === ''
  const fige = lectureSeule || sansIdentifiant

  // Un prélèvement se compte en ENVELOPPES de 500 €, pas en euros : `prelevCount`
  // vaut `Math.round(prelev / 500)`, donc un prélèvement de moins de 250 € donne
  // zéro enveloppe pour un montant non nul. On l'a longtemps rendu en boucle
  // vide — un montant annoncé, et sous lui, rien du tout.
  //
  // Le plafond n'est pas de la coquetterie : `prelevCount` est dérivé d'une
  // colonne du CSV, et un décalage de colonnes (c'est déjà arrivé sur les
  // annulés) y poserait un Gencod à treize chiffres. `Array.from` sur deux
  // milliards d'éléments ne ralentit pas l'écran, il tue l'onglet.
  const enveloppesAnnoncees = Math.max(0, Math.trunc(op.prelevCount))
  const enveloppes = Math.min(enveloppesAnnoncees, PLAFOND_ENVELOPPES)
  const tropDEnveloppes = enveloppesAnnoncees > PLAFOND_ENVELOPPES
  const prelevSansEnveloppe = op.prelev > 0 && enveloppes === 0

  const items: Array<{ k: string; v: string; s?: string; couleur?: string }> = [
    { k: 'FDC', v: fmtEur(op.fdc) },
    ...(op.reglEsp > 0 ? [{ k: 'Espèces', v: fmtEur(op.reglEsp) }] : []),
    ...(op.prelev > 0 ? [{
      k: 'Prélèv.',
      v: fmtEur(op.prelev),
      s: op.prelevIrregular
        ? `⚠ non multiple de ${EUROS_PAR_ENVELOPPE} €`
        : `${enveloppes} enveloppe${enveloppes > 1 ? 's' : ''}`,
      ...(op.prelevIrregular ? { couleur: 'var(--stop)' } : {}),
    }] : []),
    {
      k: 'Déclaré', v: fmtEur(op.decl),
      ...(auDessus ? { couleur: 'var(--warn)', s: `au-dessus de ${fmtEur(seuil)}` } : {}),
    },
    // L'écart est TOUJOURS affiché, et jamais coloré selon son signe : un
    // excédent de 50 € est la même anomalie qu'un manque de 50 €, et souvent la
    // même cause. Le masquer quand il est nul laissait croire à une absence de
    // donnée là où il y a une concordance — le vert n'a qu'ici sa place.
    { k: 'Écart', v: fmtEcart(op.diff), s: motEcart(op.diff), couleur: couleurEcart(op.diff) },
  ]

  return (
    <div style={{ padding: 10 }}>
      <Kpis items={items} />

      {auDessus && (
        <Notice c="warn">
          Déclaration au-dessus du seuil de {fmtEur(seuil)} — contrôle renforcé.
        </Notice>
      )}

      {sansIdentifiant && (
        <Notice c="stop">
          Ce caissier n’a pas d’identifiant dans le rapport opérateur : le contrôle des
          enveloppes est fermé, faute de savoir où l’écrire sans écraser celui d’un autre.
          Ré-importe le rapport opérateur à l’étape 01 pour le rétablir.
        </Notice>
      )}

      {prelevSansEnveloppe && (
        <Notice c="stop">
          Prélèvement de {fmtEur(op.prelev)} annoncé, mais moins d’une enveloppe complète
          ({fmtEur(EUROS_PAR_ENVELOPPE)} l’unité) : rien à contrôler ici, et un prélèvement qui
          ne tombe pas juste est en soi une anomalie à signaler.
        </Notice>
      )}

      {tropDEnveloppes && (
        <Notice c="stop">
          {enveloppesAnnoncees} enveloppes annoncées pour {fmtEur(op.prelev)} — le rapport
          opérateur n’est pas crédible sur cette ligne. Seules les {PLAFOND_ENVELOPPES} premières
          sont affichées ; vérifie l’import avant de contrôler quoi que ce soit.
        </Notice>
      )}

      {enveloppes === 0
        ? (prelevSansEnveloppe ? null : <Vide>Aucun prélèvement.</Vide>)
        : Array.from({ length: enveloppes }, (_, i) => {
          const env = objet(etat[`prelev_${i}`])
          const manquant = env.manquant === true
          return (
            <div key={i} style={{
              border: `1px solid ${manquant ? 'var(--stop-b)' : 'var(--line)'}`,
              background: manquant ? 'var(--stop-w)' : 'var(--surface2)',
              padding: '8px 10px', marginBottom: 6,
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: manquant ? 'var(--stop)' : undefined }}>
                {enveloppes > 1 ? `Enveloppe ${i + 1} / ${enveloppes}` : 'Enveloppe'}
              </span>
              <span className="mo hint">{fmtEur(EUROS_PAR_ENVELOPPE)}</span>
              <button className="btn stop sm" disabled={fige}
                      aria-pressed={manquant}
                      title="Déclarer cette enveloppe manquante — recliquer annule"
                      data-encours={enCours(`manquant_${i}`)}
                      onClick={() => void lancer(`manquant_${i}`,
                        () => majEnveloppe(op.id, i, 'manquant', !manquant))}>
                Manquante
              </button>
              {!manquant && (
                <>
                  <Trio libelle="Reçue" valeur={env.recu} defautCoche={false} basculable
                        lectureSeule={fige} cle={`recu_${i}`} enCours={enCours} lancer={lancer}
                        poser={(v) => majEnveloppe(op.id, i, 'recu', v)} />
                  <Trio libelle="Languette" valeur={env.languette} defautCoche basculable={false}
                        lectureSeule={fige} cle={`languette_${i}`} enCours={enCours} lancer={lancer}
                        poser={(v) => majEnveloppe(op.id, i, 'languette', v)} />
                  <Trio libelle="Signature" valeur={env.signature} defautCoche={false} basculable
                        lectureSeule={fige} cle={`signature_${i}`} enCours={enCours} lancer={lancer}
                        poser={(v) => majEnveloppe(op.id, i, 'signature', v)} />
                </>
              )}
            </div>
          )
        })}
    </div>
  )
}

/**
 * Trois états : coché, barré, rien.
 *
 * Deux asymétries de la V2, gardées telles quelles parce qu'elles portent du
 * sens. La languette part COCHÉE (`defautCoche`) : elle est conforme sauf
 * mention contraire, on ne coche donc que l'anomalie. Et elle n'est pas
 * `basculable` : recliquer ✓ ou ✗ n'efface pas la réponse, on ne peut que
 * changer d'avis. « Reçue » et « Signature », elles, s'effacent en recliquant
 * l'état actif — elles partent d'aucune réponse, et revenir à « pas encore
 * regardé » doit rester possible.
 */
function Trio({ libelle, valeur, defautCoche, basculable, lectureSeule, cle, enCours, lancer, poser }: {
  libelle: string
  valeur: unknown
  defautCoche: boolean
  basculable: boolean
  lectureSeule: boolean
  cle: string
  enCours: (cle: string) => 'true' | undefined
  lancer: (cle: string, action: () => Promise<unknown> | void) => Promise<void>
  poser: (v: boolean | undefined) => Promise<void>
}) {
  const oui = defautCoche ? valeur !== false : valeur === true
  const non = valeur === false
  // Le ton dit le sens, `aria-pressed` dit le choix retenu — c'est le CSS qui
  // remplit le contour, pas un style en ligne recopié à chaque emploi.
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span className="lbl">{libelle}</span>
      <button className="btn ok sm" disabled={lectureSeule} aria-pressed={oui}
              title={`${libelle} — conforme`} aria-label={`${libelle} conforme`}
              data-encours={enCours(`${cle}_oui`)}
              onClick={() => void lancer(`${cle}_oui`,
                () => poser(basculable && valeur === true ? undefined : true))}>
        ✓
      </button>
      <button className="btn stop sm" disabled={lectureSeule} aria-pressed={non}
              title={`${libelle} — anomalie`} aria-label={`${libelle} en anomalie`}
              data-encours={enCours(`${cle}_non`)}
              onClick={() => void lancer(`${cle}_non`,
                () => poser(basculable && non ? undefined : false))}>
        ✗
      </button>
    </div>
  )
}

// ── Phase 2 · le scan ───────────────────────────────────────────────────────

function PhaseScan({ opsScan, replies, statutDe, stats, lectureSeule, basculerRepli, valider, revenir, continuer }: {
  opsScan: Array<Operateur & { aScanner: Paiement[] }>
  replies: Set<number>
  statutDe: (id: string) => string | null
  stats: { total: number; scannes: number }
  lectureSeule: boolean
  basculerRepli: (num: number) => void
  valider: (id: string) => Promise<void>
  revenir: () => void
  continuer: () => void
}) {
  const { enCours, lancer } = useEnCours()
  const restants = stats.total - stats.scannes
  return (
    <>
      {/* « 0 sur 0 » n'est pas un travail fini : sans rien à scanner, le compteur
          reste neutre. Le vert se mérite. */}
      <Kpis items={[
        {
          k: 'Scannés', v: `${stats.scannes} / ${stats.total}`,
          ...(stats.total > 0 && restants === 0 ? { couleur: 'var(--ok)' } : {}),
        },
        { k: 'Restants', v: restants, ...(restants > 0 ? { couleur: 'var(--warn)' } : {}) },
        { k: 'Opérateurs concernés', v: opsScan.length },
      ]} />

      {opsScan.length === 0
        ? <Vide>Aucun TRP / CAP à scanner pour cette journée.</Vide>
        : opsScan.map((op) => {
          const faits = op.aScanner.filter((p) => statutDe(p.id) === 'valide').length
          const tout = faits === op.aScanner.length
          const replie = replies.has(op.num)
          return (
            <Card key={op.num} lampe={tout ? 'ok' : 'act'} style={{ marginBottom: 8 }}
                  titre={`#${op.num} · ${nomCourt(op.nom)}`}
                  actions={
                    <>
                      {faits > 0 && <Chip c="ok">{faits} SCANNÉS</Chip>}{' '}
                      {op.aScanner.length - faits > 0 && (
                        <Chip c="warn">{op.aScanner.length - faits} RESTANT{op.aScanner.length - faits > 1 ? 'S' : ''}</Chip>
                      )}{' '}
                      <button className="btn gh sm" aria-expanded={!replie}
                              onClick={() => basculerRepli(op.num)}>
                        {replie ? 'Déplier la liste' : 'Replier la liste'}
                      </button>
                    </>
                  }>
              {!replie && op.aScanner.map((p) => {
                const valide = statutDe(p.id) === 'valide'
                return (
                  <div className="slot" key={p.id} style={{
                    boxShadow: `inset 3px 0 0 ${valide ? 'var(--ok)' : 'transparent'}`,
                    background: valide ? 'var(--ok-w)' : undefined,
                  }}>
                    <BadgeType reglementId={p.reglementId} />
                    <span className="mo" style={{ fontSize: 11.5, color: 'var(--ink3)' }}>#{p.ticket}</span>
                    <span className="hint">{p.heure}</span>
                    <span className="sp" />
                    <span className="mo" style={COL_MONTANT}>{signe(p.montant)}</span>
                    {valide
                      ? <Chip c="ok">VALIDÉ</Chip>
                      : lectureSeule
                        ? <Chip>À SCANNER</Chip>
                        : (
                          <button className="btn ok sm" data-encours={enCours(p.id)}
                                  title="Valider ce règlement à la main — aucune session Honeywell n’est armée"
                                  onClick={() => void lancer(p.id, () => valider(p.id))}>
                            Valider le règlement
                          </button>
                        )}
                  </div>
                )
              })}
            </Card>
          )
        })}

      <div style={{ marginTop: 12 }}>
        <Chantier titre="L’armement d’une session Honeywell">
          La V2 recevait ici les validations poussées par le module Honeywell. Le temps réel
          fonctionne — une validation écrite dans <span className="mo">rapport.verifications</span> par
          un autre poste arrive tout de suite sur cet écran. Ce qui manque, c’est l’armement d’une
          session depuis FlashCP (<span className="mo">fc_hw_sessions</span>, phase 3, points ouverts
          O2 et O3) : en attendant, on valide à la main, ligne par ligne.
        </Chantier>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button className="btn" onClick={revenir}>← Retour aux caissiers</button>
        <span className="sp" />
        <button className="btn pri" onClick={continuer}>Terminer le scan →</button>
      </div>
    </>
  )
}

// ── Phase 3 · les décisions de remise ───────────────────────────────────────

function PhaseResume({ resumeParOp, total, aDecider, paymentMeta, lectureSeule, decider, revenir, continuer }: {
  resumeParOp: Array<Operateur & { manquants: Paiement[] }>
  total: number
  aDecider: number
  paymentMeta: Record<string, unknown>
  lectureSeule: boolean
  decider: (id: string, inclure: boolean) => Promise<void>
  revenir: () => void
  continuer: () => void
}) {
  const { enCours, lancer } = useEnCours()
  return (
    <>
      {/* Un règlement non validé est une non-concordance entre ce que la caisse
          a enregistré et ce qui est dans l'enveloppe : il se compte comme un
          écart en unités, et ne redevient vert qu'à zéro. */}
      <Kpis items={[
        { k: 'Non validés', v: total, couleur: couleurEcartUnites(total) },
        { k: 'À décider', v: aDecider, couleur: couleurEcartUnites(aDecider) },
        { k: 'Opérateurs concernés', v: resumeParOp.length },
      ]} />

      {aDecider > 0 && (
        <Notice c="warn">
          <b>{aDecider} règlement{aDecider > 1 ? 's' : ''} sans décision.</b> Un règlement non validé
          et non décidé n’entre ni ne sort de la remise : il reste en suspens, et c’est la remise du
          jour qui s’en trouve fausse.
        </Notice>
      )}

      {resumeParOp.length === 0
        ? <Vide>Tous les règlements du jour sont validés.</Vide>
        : resumeParOp.map((op) => (
          <Card key={op.num} lampe="warn" style={{ marginBottom: 8 }}
                titre={`#${op.num} · ${nomCourt(op.nom)}`}
                actions={<Chip c="warn">{op.manquants.length} MANQUANT{op.manquants.length > 1 ? 'S' : ''}</Chip>}>
            {op.manquants.map((p) => {
              const decision = objet(paymentMeta[p.id]).addToRemise
              const intact = decision === undefined
              return (
                <div className="slot" key={p.id} style={{
                  boxShadow: `inset 3px 0 0 ${intact ? 'var(--warn)' : 'transparent'}`,
                  background: intact ? 'var(--warn-w)' : undefined,
                }}>
                  <BadgeType reglementId={p.reglementId} />
                  <span className="mo" style={{ fontSize: 11.5, color: 'var(--ink3)' }}>#{p.ticket}</span>
                  {intact && <Chip c="warn">À DÉCIDER</Chip>}
                  <span className="sp" />
                  <span className="mo" style={COL_MONTANT}>{signe(p.montant)}</span>
                  {lectureSeule ? (
                    <Chip c={decision === true ? 'ok' : decision === false ? 'stop' : 'neutre'}>
                      {decision === true ? 'INCLUS' : decision === false ? 'EXCLU' : 'SANS DÉCISION'}
                    </Chip>
                  ) : (
                    <>
                      <button className="btn ok sm" aria-pressed={decision === true}
                              data-encours={enCours(`${p.id}_in`)}
                              onClick={() => void lancer(`${p.id}_in`, () => decider(p.id, true))}>
                        Inclure à la remise
                      </button>
                      <button className="btn stop sm" aria-pressed={decision === false}
                              data-encours={enCours(`${p.id}_out`)}
                              onClick={() => void lancer(`${p.id}_out`, () => decider(p.id, false))}>
                        Exclure de la remise
                      </button>
                    </>
                  )}
                </div>
              )
            })}
          </Card>
        ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button className="btn" onClick={revenir}>← Retour au scan</button>
        <span className="sp" />
        <button className="btn pri" onClick={continuer}>Terminer la session →</button>
      </div>
    </>
  )
}

// ── Phase 4 · la fin de session ─────────────────────────────────────────────

function PhaseFin({ stats, recommencer, quitter }: {
  stats: { valides: number; inclus: number; exclus: number; aDecider: number; total: number }
  recommencer: () => void
  quitter: () => void
}) {
  const restants = stats.total - stats.valides
  // « 0 sur 0 » n'est pas un dépouillement terminé, c'est un dépouillement qui
  // n'a jamais eu lieu : la lampe reste neutre et le compte cède la place au
  // motif. Un état vide n'est pas un état conforme.
  const rien = stats.total === 0

  // La lampe parle de la SESSION, pas des écarts : elle est verte quand plus
  // rien n'attend une décision — un règlement non validé mais décidé est une
  // issue légitime. Le compte, lui, garde la couleur de son écart : les deux
  // disent des choses différentes et toutes les deux vraies.
  return (
    <Card titre="Session clavier terminée"
          lampe={rien ? 'neutre' : stats.aDecider === 0 ? 'ok' : 'warn'}>
      <div style={{ padding: 16, textAlign: 'center' }}>
        {rien ? (
          <Vide>
            Aucun règlement hors PAD sur cette journée : il n’y avait rien à dépouiller au clavier.
          </Vide>
        ) : (
          <>
            <div className="big" style={{ fontSize: 30, color: couleurEcartUnites(restants) }}>
              {stats.valides} / {stats.total}
            </div>
            <div className="hint" style={{ marginBottom: 14 }}>
              règlements validés{restants > 0 ? ` — ${restants} non validé${restants > 1 ? 's' : ''}` : ''}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
              {stats.inclus > 0 && <Chip>{stats.inclus} NON CONFORMES INCLUS</Chip>}
              {stats.exclus > 0 && <Chip c="stop">{stats.exclus} EXCLUS DE LA REMISE</Chip>}
              {stats.aDecider > 0 && <Chip c="warn">{stats.aDecider} À DÉCIDER</Chip>}
            </div>
          </>
        )}

        <div className="hint" style={{ marginBottom: 14 }}>
          Le wizard ne valide pas l’étape : c’est la barre d’action de la branche 05a qui le fait,
          quand tu juges le dépouillement fini.
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn" onClick={recommencer}>Recommencer une session</button>
          <button className="btn pri" onClick={quitter}>↩ Revenir au dépouillement</button>
        </div>
      </div>
    </Card>
  )
}

// ── Petits morceaux ─────────────────────────────────────────────────────────

function BadgeType({ reglementId }: { reglementId: number | null }) {
  const info = reglementId == null ? undefined : LIBELLES_TYPE[reglementId]
  if (!info) return null
  const c = COULEURS_TYPE[info.court]
  const detail = detailType(info.nom, info.court)
  // Le code de famille est toujours au même endroit, en chasse fixe : c'est LUI
  // qui distingue, la teinte ne fait que confirmer. En peau registre, --fam-bon
  // et --fam-cap sont trop voisins pour porter seuls la différence.
  return (
    <span className="chip" title={info.nom}
          style={c ? { background: c.fond, borderColor: c.trait, color: c.encre } : undefined}>
      <b className="mo">{info.court}</b>{detail ? ` ${detail}` : ''}
    </span>
  )
}

function Aide({ fermer }: { fermer: () => void }) {
  const raccourcis: Array<[string, string]> = [
    ['Entrée', 'Valider le montant saisi — le type est détecté tout seul'],
    ['+ +', 'Solder le caissier et passer au suivant — double appui, il marque le reste NON CONFORME'],
    ['1, 2, 3…', 'Choisir le type quand deux règlements portent le même montant'],
    ['Échap', 'Désarmer le « + », puis vider le champ, puis revenir au dépouillement'],
    ['← (champ vide)', 'Caissier précédent'],
    ['→ (champ vide)', 'Caissier suivant, sans rien marquer'],
    ['?', 'Ouvrir ces raccourcis'],
  ]
  return (
    <Modale titre="Raccourcis clavier"
            sous="Le seul écran de FlashCP où le clavier commande. Ailleurs, tout se fait à la souris."
            fermer={fermer}>
      {raccourcis.map(([touche, quoi]) => (
        <div key={touche} style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 8 }}>
          <span className="mo" style={{
            fontSize: 11, padding: '2px 7px', border: '1px solid var(--line2)',
            background: 'var(--surface2)', whiteSpace: 'nowrap', flexShrink: 0,
          }}>{touche}</span>
          <span style={{ fontSize: 12.5, color: 'var(--ink2)' }}>{quoi}</span>
        </div>
      ))}
      <div className="notice" style={{ marginTop: 12, marginBottom: 0 }}>
        <Lamp c="act" />
        <div className="sp">
          Saisis le montant <b>en centimes</b> — <span className="mo">2234</span> vaut 22,34 € — puis
          Entrée. Deux règlements au même montant : la barre passe au choix de type, tu réponds 1, 2
          ou 3.
        </div>
      </div>
      <div className="btns">
        {/* Autofocus : la modale s'ouvre au clavier (« ? »), elle doit se
            refermer au clavier — Entrée aussi bien qu'Échap. */}
        <button className="btn" autoFocus onClick={fermer}>Fermer</button>
      </div>
    </Modale>
  )
}
